import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

interface BandwidthHelpDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * Help dialog explaining bandwidth commitment calculation methods
 * - Monthly commitment (reset each month)
 * - Total commitment (deducted gradually)
 */
export function BandwidthHelpDialog({
    open,
    onOpenChange,
}: BandwidthHelpDialogProps) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">
                        Hướng dẫn Cam kết Bandwidth
                    </DialogTitle>
                    <DialogDescription className="text-base">
                        Cách tính giá bandwidth dựa trên giá trị cam kết và thời
                        gian cam kết
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-5 py-4">
                    <div>
                        <h3 className="font-semibold text-lg mb-2">
                            Cam kết Bandwidth là gì?
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            Cam kết bandwidth là mức dung lượng băng thông mà
                            khách hàng cam kết sử dụng trong một khoảng thời
                            gian nhất định. Khách hàng sẽ được áp dụng giá ưu
                            đãi cho lượng cam kết này.
                        </p>
                    </div>

                    <div className="border-t pt-4">
                        <h3 className="font-semibold text-lg mb-3">
                            2 Cách tính Cam kết:
                        </h3>

                        <div className="space-y-4">
                            {/* Case 1: Monthly */}
                            <MonthlyCommitmentCard />

                            {/* Case 2: Total */}
                            <TotalCommitmentCard />
                        </div>
                    </div>

                    <ImportantNotesCard />
                    <ComparisonTableCard />
                </div>
            </DialogContent>
        </Dialog>
    )
}

/** Monthly commitment explanation card */
function MonthlyCommitmentCard() {
    return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <span className="bg-green-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                    1
                </span>
                Cam kết theo tháng
            </h4>
            <div className="space-y-2 text-sm text-green-800 ml-8">
                <p className="font-medium">Ví dụ: Cam kết 1TB / 1 tháng</p>
                <p>
                    → <strong>Mỗi tháng</strong> khách hàng được sử dụng{' '}
                    <strong>1TB</strong>
                </p>
                <p>
                    → Tháng nào cũng <strong>reset lại</strong>, được dùng
                    1TB/tháng
                </p>
                <p>
                    → Nếu tháng nào vượt quá 1TB thì tính phí vượt cho tháng đó
                </p>
                <div className="bg-white rounded p-3 mt-2 border border-green-300">
                    <p className="font-semibold mb-1">Ví dụ cụ thể:</p>
                    <ul className="space-y-1">
                        <li>• Tháng 1: Dùng 0.8TB → Không tính vượt</li>
                        <li>• Tháng 2: Dùng 1.2TB → Tính vượt 0.2TB</li>
                        <li>
                            • Tháng 3: Dùng 0.9TB → Không tính vượt (reset lại
                            từ đầu)
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    )
}

/** Total commitment explanation card */
function TotalCommitmentCard() {
    return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
                    2
                </span>
                Cam kết theo tổng (dùng dần)
            </h4>
            <div className="space-y-2 text-sm text-blue-800 ml-8">
                <p className="font-medium">Ví dụ: Cam kết 12TB / 12 tháng</p>
                <p>
                    → Tổng cộng <strong>12TB cho cả 12 tháng</strong>
                </p>
                <p>
                    → Mỗi tháng đối soát và <strong>trừ dần</strong> từ 12TB này
                </p>
                <p>→ Khi tổng vượt quá 12TB (tích lũy) thì bắt đầu tính vượt</p>
                <div className="bg-white rounded p-3 mt-2 border border-blue-300">
                    <p className="font-semibold mb-1">Ví dụ cụ thể:</p>
                    <ul className="space-y-1">
                        <li>• Tháng 1: Dùng 0.8TB → Còn lại 11.2TB</li>
                        <li>• Tháng 2: Dùng 1.2TB → Còn lại 10TB</li>
                        <li>• Tháng 3: Dùng 0.9TB → Còn lại 9.1TB</li>
                        <li>• ... (tiếp tục trừ dần)</li>
                        <li>
                            • Tháng 11: Đã dùng hết 12TB → Tháng 11,12 tính vượt
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    )
}

/** Important notes card */
function ImportantNotesCard() {
    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-amber-900 mb-2">
                ⚠️ Lưu ý quan trọng:
            </h4>
            <ul className="space-y-1 text-sm text-amber-800">
                <li>
                    • <strong>Thời gian cam kết = 1</strong>: Tính theo tháng,
                    mỗi tháng reset lại
                </li>
                <li>
                    • <strong>Thời gian cam kết &gt; 1</strong>: Tính theo tổng,
                    trừ dần qua các tháng
                </li>
                <li>• Giá cam kết thường rẻ hơn giá vượt cam kết</li>
                <li>
                    • Phần vượt cam kết sẽ áp dụng bảng giá vượt (overage
                    pricing)
                </li>
            </ul>
        </div>
    )
}

/** Comparison table card */
function ComparisonTableCard() {
    return (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-purple-900 mb-2">
                💡 So sánh 2 cách:
            </h4>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-purple-300">
                            <th className="text-left py-2 text-purple-900">
                                Tiêu chí
                            </th>
                            <th className="text-left py-2 text-purple-900">
                                Theo tháng (1 tháng)
                            </th>
                            <th className="text-left py-2 text-purple-900">
                                Theo tổng (12 tháng)
                            </th>
                        </tr>
                    </thead>
                    <tbody className="text-purple-800">
                        <tr className="border-b border-purple-200">
                            <td className="py-2">Reset</td>
                            <td className="py-2">Mỗi tháng</td>
                            <td className="py-2">Không reset</td>
                        </tr>
                        <tr className="border-b border-purple-200">
                            <td className="py-2">Linh hoạt</td>
                            <td className="py-2">
                                Cao (tháng này ít, tháng kia nhiều)
                            </td>
                            <td className="py-2">Thấp (tổng cố định)</td>
                        </tr>
                        <tr>
                            <td className="py-2">Phù hợp</td>
                            <td className="py-2">Traffic đều đặn hàng tháng</td>
                            <td className="py-2">
                                Traffic không đều, tiết kiệm dài hạn
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    )
}
